import React from 'react'

import { API } from '../../revelry_api'
import { ShoppingCart, FlowerProduct, MemorialTree } from '../../types'
import useCartID from '../../hooks/useCartID'
import EventTrackingModel from '../../components/models/EventTrackingModel'
import CartModel from '../../components/models/CartModel'

export const DEFAULT_CART: ShoppingCart = {
  errors: [],
  facility_id: null,
  flower_order_total_in_cents: 0,
  flower_shopping_cart_items: [],
  memorial_tree_shopping_cart_items: [],
  external_id: '',
  id: -1,
}

export interface ICartContext {
  addProductToCart: (product: FlowerProduct) => Promise<ShoppingCart>
  addTreeProductToCart: (product: MemorialTree) => Promise<ShoppingCart>
  cart: ShoppingCart
  cartID: string
  createCart: () => Promise<ShoppingCart>
  isChildOfProvider: boolean
  loading: boolean
  removeProductFromCart: (product: FlowerProduct) => Promise<ShoppingCart>
  removeTreeProductFromCart: (product: MemorialTree) => Promise<ShoppingCart>
  totalProductQuantity: number
}

const CartContext = React.createContext<ICartContext>({
  addProductToCart: () => Promise.resolve(DEFAULT_CART),
  addTreeProductToCart: () => Promise.resolve(DEFAULT_CART),
  cart: DEFAULT_CART,
  cartID: '',
  createCart: () => Promise.resolve(DEFAULT_CART),
  isChildOfProvider: false,
  loading: false,
  removeProductFromCart: () => Promise.resolve(DEFAULT_CART),
  removeTreeProductFromCart: () => Promise.resolve(DEFAULT_CART),
  totalProductQuantity: 0,
})

export const CartContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [loading, setLoading] = React.useState(true)
  const [cart, setCart] = React.useState<ShoppingCart>(DEFAULT_CART)
  const { cartID, setCartID } = useCartID()
  const loadCart = async (cID: string) => {
    try {
      const cartResponse = await API.carts.load(cID)
      setCart(cartResponse)
    } finally {
      setLoading(false)
    }
  }
  const createCart = async () => {
    setLoading(true)

    try {
      const cartResponse = await API.carts.create()

      setCartID(cartResponse.external_id)

      return cartResponse
    } finally {
      setLoading(false)
    }
  }
  const addProductToCart = async (product: FlowerProduct): Promise<ShoppingCart> => {
    setLoading(true)

    try {
      if (cartID) {
        const updatedCart = await API.carts.addFlower(cartID, product)
        setCart(updatedCart)

        EventTrackingModel.trackFlowerAdded(product)

        return updatedCart
      } else {
        const cartResponse = await API.carts.create()
        const updatedCart = await API.carts.addFlower(cartResponse.external_id, product)

        setCartID(cartResponse.external_id)
        setCart(updatedCart)

        return updatedCart
      }
    } finally {
      setLoading(false)
    }
  }
  const addTreeProductToCart = async (product: MemorialTree): Promise<ShoppingCart> => {
    setLoading(true)

    try {
      if (cartID) {
        const updatedCart = await API.carts.addTree(cartID, product)
        setCart(updatedCart)

        EventTrackingModel.trackTreeAdded(product)

        return updatedCart
      } else {
        const cartResponse = await API.carts.create()
        const updatedCart = await API.carts.addTree(cartResponse.external_id, product)

        setCartID(cartResponse.external_id)
        setCart(updatedCart)

        return updatedCart
      }
    } finally {
      setLoading(false)
    }
  }
  const removeProductFromCart = async (product: FlowerProduct): Promise<ShoppingCart> => {
    setLoading(true)

    try {
      if (cartID) {
        const updatedCart = await API.carts.removeFlower(cartID, product)
        setCart(updatedCart)
        EventTrackingModel.trackFlowerRemoved(product)

        return updatedCart
      }
    } finally {
      setLoading(false)
    }
  }
  const removeTreeProductFromCart = async (product: MemorialTree): Promise<ShoppingCart> => {
    setLoading(true)

    try {
      if (cartID) {
        const updatedCart = await API.carts.removeTree(cartID, product.product_id)
        setCart(updatedCart)
        EventTrackingModel.trackTreeRemoved(product)

        return updatedCart
      }
    } finally {
      setLoading(false)
    }
  }
  const totalProductQuantity = CartModel.totalProductQuantity(cart)

  React.useEffect(() => {
    if (cartID) {
      loadCart(cartID)
    } else {
      setLoading(false)
    }
  }, [cartID])

  return (
    <CartContext.Provider
      value={{
        addProductToCart,
        addTreeProductToCart,
        cart,
        cartID,
        createCart,
        isChildOfProvider: true,
        loading,
        removeProductFromCart,
        removeTreeProductFromCart,
        totalProductQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export default CartContext
